import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { extensionInstallIdSchema } from "./claim-request";

/**
 * POST /auto-apply/extension/heartbeat — "I am installed and running".
 *
 * Deliberately NOT a claim. `ExtensionSession.lastSeenAt` is the routing
 * signal the submission worker reads via `hasLiveExtensionSession()`; a
 * heartbeat only advances `lastHeartbeatAt`, which nothing routes on. See the
 * comment block on the heartbeat handler in `extension.service.ts` for why
 * conflating the two produces a refund-and-notification storm.
 *
 * There is no `userId` here on purpose: the session is scoped to the
 * authenticated caller, so a client cannot heartbeat on someone else's behalf.
 */
export const extensionHeartbeatRequestSchema = z.object({
  installId: extensionInstallIdSchema,
  /** Reported for diagnostics only — not persisted, never used for gating. */
  extensionVersion: z.string().max(32).optional(),
  /** Owned server batch run whose lease should be renewed; never starts work. */
  batchRunId: z.string().min(1).optional(),
});

export class ExtensionHeartbeatRequestDto extends createZodDto(extensionHeartbeatRequestSchema) {}

export const extensionHeartbeatResponseSchema = z.object({
  /**
   * False when the write was debounced (a heartbeat for this install landed
   * inside EXTENSION_HEARTBEAT_MIN_WRITE_INTERVAL_SECONDS). The heartbeat was
   * still accepted — the client must not retry on `false`.
   */
  recorded: z.boolean(),
  /** Persisted presence stamp after this call — never `lastSeenAt`. */
  lastHeartbeatAt: z.string().datetime(),
});

export type ExtensionHeartbeatResponseDto = z.infer<typeof extensionHeartbeatResponseSchema>;
